
import { computed, defineComponent, PropType, reactive, Ref, ref, toRaw, watch } from 'vue'

// Components
import Loader from '@/common/components/UI/Loader/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'

// Class
import { DataState } from '@/core/classes/DataState.class'

// Realisation
import { delivery } from '@/core/realisations/delivery.realisation'

// Types
import { DeliveryPointT, DeliveryTariffT } from '@/core/types/delivery.types'

// Composable
import declOfNum from '@/common/helpers/declOfNum'

export default defineComponent({
    components: {
        loader: Loader,
        'failed-fetch': FailedFetch,
        'the-button': TheButton,
        cheap: Cheap,
    },
    props: {
        opened: {
            type: Boolean,
            default: false,
        },
        stepNumber: {
            type: Number,
            default: 0,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        points: {
            type: Array as PropType<DeliveryPointT[]>,
            default: () => [],
        },
    },
    emits: ['submit', 'reset'],
    setup(props, { emit }) {
        const outsideZoneMessage = ref(null) as Ref<string | null | undefined>

        const state = reactive(new DataState<DeliveryTariffT>({}))

        const additional_data = reactive({
            cityName: null as string | null,
            distance: null as string | null,
            maxHubCoef: null as string | null,
        })

        const form_state = reactive({
            selected_tariff: null as DeliveryTariffT | null,
        })

        const loadTariffs = async () => {
            try {
                state.is_loading = true

                const response = await delivery.getTariffs(props.points)
                state.list = response.data.tariffs

                additional_data.cityName = response.data.cityName
                additional_data.distance = response.data.distance
                additional_data.maxHubCoef = response.data.maxHubCoef

                state.is_failed = false
                state.failure.error_state.is_cancel = false
                state.is_completed = true
            } catch (error) {
                state.failure.catchError(error)

                if (!state.failure.error_state.is_cancel) {
                    // @ts-ignore
                    if (error.length && error[0]) {
                        // @ts-ignore
                        outsideZoneMessage.value = error[0]
                    } else {
                        outsideZoneMessage.value = state.failure.error_state.message
                    }

                    state.is_failed = true
                }
            } finally {
                if (!state.failure.error_state.is_cancel) {
                    state.is_loading = false
                }
            }
        }

        const onSelectTariff = (tariff: DeliveryTariffT) => {
            form_state.selected_tariff = tariff

            emit('submit', {
                transport: form_state.selected_tariff.transport,
                price: form_state.selected_tariff.price,
                is_fastest: tariff.isFastest,
            })
        }

        const resetSelectedTariff = () => {
            form_state.selected_tariff = null
            emit('reset')
        }

        watch(
            () => props.opened,
            (opened) => {
                if (!opened) {
                    outsideZoneMessage.value = null

                    state.pagination.current_page = 1
                    state.pagination.total_pages = 1
                    state.list = []

                    additional_data.cityName = null
                    additional_data.distance = null
                    additional_data.maxHubCoef = null

                    form_state.selected_tariff = null
                }
            }
        )

        watch(
            () => props.points.length,
            () => {
                loadTariffs()
            }
        )

        const isLoading = computed(() => state.is_loading && !state.is_failed)
        const isEmpty = computed(
            () => !state.is_loading && !state.is_failed && state.list.length === 0
        )
        const isTariffSelected = computed(() => form_state.selected_tariff)
        const isCanRenderList = computed(
            () => !isLoading.value && !isEmpty.value && !isTariffSelected.value
        )

        return {
            state,
            additional_data,
            form_state,
            loadTariffs,
            onSelectTariff,
            resetSelectedTariff,
            isLoading,
            isEmpty,
            isTariffSelected,
            isCanRenderList,
            declOfNum,
            outsideZoneMessage,
        }
    },
})
